'use strict';

// external imports

// shared imports
import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from "~/shared/src/utilities/metaModel";
import {__} from "~/shared/src/utilities/messageCatalog";
import Status from "~/shared/src/mm/Status";
import ValueSet from "~/shared/src/mm/ValueSet";

// backend imports


// type definitions
export type ValueAttributes = {
    id? : number;
    statusId? : number;
    origin? : string;
    module? : string;
    valueSetId : number;
    value? : string;
    sortOrder? : number;
    uuid? : string;
    modifierUserId? : string;
    modificationTime? : NDate;
    validFromTime? : NDate;
    validToTime? : NDate;
};

export type ValueAttributesCollection = Array<ValueAttributes>;

export type ValueAttributesRequired = keyof Pick<ValueAttributes, 'id' | 'statusId' | 'valueSetId' | 'value'>;

export type ValueAttributesSettable = Pick<ValueAttributes, 'statusId' | 'origin' | 'module' | 'value' | 'sortOrder'>;

/**
 * The Value class.
 */
class Value {

    /**
     * The unique Id counter for Values
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the Value
     */
    readonly id : number;

    /**
     * The StatusID of the Value
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        this.validateStatusId( value );

        this.handleModified( 'statusId', value );

        this._statusId = value;
    }

    /**
     * The origin of this
     */
    protected _origin : string = 'FSZ';
    get origin() : string {
        return this._origin;
    }

    set origin( value : string ) {
        this.validateOrigin( value );

        this.handleModified( 'origin', value );

        this._origin = value;
    }

    /**
     * The module this belongs to
     * @protected
     */
    protected _module : string = 'base';
    get module() : string {
        return this._origin;
    }

    set module( value : string ) {
        this.validateModule( value );

        this.handleModified( 'module', value );

        this._module = value;
    }

    /**
     * The ValueSetID of the Value
     */
    readonly valueSetId : number;

    /**
     * The value of the Value
     */
    protected _value : string = '';
    get value() {
        return this._value;
    }

    set value( value : string ) {
        this.validateValue( value );

        this.handleModified( 'value', value );

        this._value = value;
    }

    /**
     * The sort order within the ValueSet
     */
    protected _sortOrder : number = 5;
    get sortOrder() {
        return this._sortOrder;
    }

    set sortOrder( value : number ) {
        this.checkStatus();

        this.handleModified( 'sortOrder', value );

        this._sortOrder = value;
    }

    /**
     * The unique spoken id of the Value
     */
    readonly uuid : string = '';

    /**
     * The UserID of the last modifier of the Value
     */
    readonly modifierUserId : string = '';

    /**
     * The last modification date of the Value
     */
    readonly modificationTime : NDate = null;

    /**
     * The valid from date of a History-Value
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-Value
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    get valueSet() : ValueSet {
        return metaModel[ this._schema ].getValueSet( this.valueSetId )!;
    }

    /**
     * The label for this
     */
    get labelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'value', this.id, 'label' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = this.value;
        }

        return retVal;
    }

    get label() {
        return __( this.labelMsgId );
    }

    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof ValueAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param schema                    The database schema the Item belongs to
     */
    constructor( attributes : ValueAttributes, schema : Schema = global.schema ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        this._schema = schema;

        this.valueSetId = attributes.valueSetId;

        if ( attributes.id ) {
            this.id = attributes.id;
        }
        else {
            this.id = Value.uniqueIdCount--;
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.origin ) {
            this._origin = attributes.origin;
        }

        if ( attributes.module ) {
            this._module = attributes.module;
        }

        if ( attributes.value ) {
            this._value = attributes.value;
        }

        if ( attributes.sortOrder ) {
            this._sortOrder = attributes.sortOrder;
        }

        if ( attributes.uuid ) {
            this.uuid = attributes.uuid;
        }

        if ( attributes.modifierUserId ) {
            this.modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            this.modificationTime = attributes.modificationTime;
        }

        if ( attributes.validFromTime ) {
            this.validFromTime = attributes.validFromTime;
        }
        if ( attributes.validToTime ) {
            this.validToTime = attributes.validToTime;
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ValueAttributes {
        // required attributes
        const retVal : ValueAttributes = {
            id        : this.id,
            valueSetId: this.valueSetId
        };

        if ( includeAttributes === 'all' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'origin' ) ) {
            retVal.origin = this._origin;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'module' ) ) {
            retVal.module = this._module;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'value' ) ) {
            retVal.value = this._value;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'sortOrder' ) ) {
            retVal.sortOrder = this._sortOrder;
        }

        // not settable attributes
        if ( includeAttributes === 'all' ) {
            retVal.uuid = this.uuid;
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = this.modificationTime;
            }

            // only history attributes
            else {
                retVal.validFromTime = this.validFromTime;
                retVal.validToTime = this.validToTime;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `Value '${this.valueSet.name}:${this.value}' (ID: ${this.id})`;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() {
        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        if ( this._attributesModified.has( 'value' ) ) {
            this._value = this._attributesModified.get( 'value' ) as string;
        }

        if ( this._attributesModified.has( 'sortOrder' ) ) {
            this._sortOrder = this._attributesModified.get( 'sortOrder' ) as number;
        }

        this._attributesModified = new Map();
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : ValueAttributesSettable = {} ) : Value {
        const clone : Value = Object.assign( {}, this );

        // apply data
        if ( attributes.statusId ) {
            clone.statusId = attributes.statusId;
        }

        if ( attributes.value ) {
            clone.value = attributes.value;
        }

        if ( attributes.sortOrder ) {
            clone.sortOrder = attributes.sortOrder;
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this.id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return this._statusId === metaModel[ this._schema ].getStatus( 'history' )?.id || this._statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id;
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof ValueAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof ValueAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError(
                'YN.base.dataObject.value.attributes.validation.couldNotChangeDeleted'
            );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.couldNotDeleteChanged'
            );
        }
    }

    /**
     * Validates the origin for this
     */
    protected validateOrigin( origin : string ) {
        this.checkStatus();

        if ( !origin.match( /^[A-Z]+$/ ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.origin.isNotUppercase' );
        }
    }

    /**
     * Validates the module for this
     */
    protected validateModule( module : string ) {
        this.checkStatus();
    }

    /**
     * Validates the name for this
     */
    protected validateValue( name : string ) {
        this.checkStatus();

        const value = metaModel[ this._schema ].getValueSetValue( this.valueSetId, name );

        if ( value && value.id !== this.id ) {
            throw new LocalizableError( 'YN.base.dataObject.value.attribute.validation.name.isNotUnique' );
        }
    }

}

export default Value;
